import { useCallback, useState } from 'react';
import useSWR, { mutate } from 'swr';
import {
	createFarm,
	CreateFarmPayload,
	deleteFarm,
	deleteFarmFile,
	fetchFarms,
	fetchSingleFarm,
	updateFarm,
	UpdateFarmPayload,
} from './myFarmService';
import { useFarmStore } from './myFarmStore';

export function useFetchFarms(pageNumber = 1, pageSize = 9) {
	const { setFarms } = useFarmStore();
	const [error, setError] = useState<string | null>(null);

	const { data, isValidating } = useSWR(
		['farms', pageNumber, pageSize],
		() => fetchFarms(pageNumber, pageSize),
		{
			shouldRetryOnError: false,
			revalidateOnFocus: false,
			onSuccess: (data) => {
				setFarms(data);
			},
			onError: (err) => {
				setError(err.message);
			},
		}
	);

	const refresh = useCallback(() => {
		mutate(['farms', pageNumber, pageSize]);
	}, [pageNumber, pageSize]);

	return {
		farms: data || null,
		isLoading: isValidating,
		error,
		refresh,
	};
}

export function useSingleFarm(farmId: string) {
	const { setSingleFarm } = useFarmStore();

	const { data, error, mutate, isValidating } = useSWR(
		`farms/${farmId}`,
		() => fetchSingleFarm(farmId),
		{
			shouldRetryOnError: false,
			revalidateOnFocus: false,
			revalidateOnReconnect: false,
			onSuccess: (data) => {
				setSingleFarm(data);
			},
		}
	);

	const refresh = () => mutate();
	const fetch = () => fetchSingleFarm(farmId);

	return {
		singleFarm: data?.data || null,
		isLoading: isValidating,
		error: error ? (error as Error).message : null,
		refresh,
		fetch,
	};
}

export function useCreateFarm() {
	const [loading, setLoading] = useState(false);
	const create = async (data: CreateFarmPayload) => {
		setLoading(true);
		try {
			const response = await createFarm(data);

			localStorage.setItem('farmId', response.data.farmID);
			mutate('farms');

			return response;
		} finally {
			setLoading(false);
		}
	};

	return { create, loading };
}

export function useUpdateFarm() {
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState<Error | null>(null);

	const update = async (data: Partial<UpdateFarmPayload>, farmId: string) => {
		setLoading(true);
		setError(null);
		try {
			const response = await updateFarm(data, farmId);

			mutate('farms');

			return response;
		} finally {
			setLoading(false);
		}
	};

	return { update, loading, error };
}

export function useDeleteFarm() {
	const [loading, setLoading] = useState(false);

	const remove = async (farmId: string) => {
		setLoading(true);

		try {
			await deleteFarm(farmId);
		} finally {
			setLoading(false);
		}
	};

	return { remove, loading };
}

export function useDeleteFarmFile() {
	const [loading, setLoading] = useState(false);

	const remove = async (fileId: string) => {
		setLoading(true);
		try {
			await deleteFarmFile(fileId);
			mutate('farms');
		} finally {
			setLoading(false);
		}
	};

	return { remove, loading };
}
